import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subscription, map } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { ClipboardService } from 'ngx-clipboard';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreateShareInvitationRequest, InvitationsService } from 'ldt-identity-service-api';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { AuthService, UIOrg } from 'src/app/auth/service/auth.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { StreamsSubscribeDrawerService } from 'src/app/shared/streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SubscriptionDialogComponent } from 'src/app/settings/billing/subscription-dialog/subscription-dialog.component';
import { InvitationsCreateDialogComponent } from 'src/app/team/invitations-create-dialog/invitations-create-dialog.component';
import { Log, LogLevel } from 'src/app/logger.service';
import * as Highcharts from 'highcharts';

@Component({
  selector: 'app-root-gotlivedata',
  templateUrl: './app.gld.component.html',
  styleUrls: ['./app.gld.component.scss'],
  standalone: false,
})
export class GotlivedataAppComponent implements OnInit, OnDestroy {
  @ViewChild(MatSidenav) sidenav: MatSidenav;

  get email(): string | undefined {
    return this.auth.email;
  }
  $isLoggedIn: Observable<boolean>;

  adminRoutes = [
    {
      path: 'admin/clean',
      name: 'Clean Service',
    },
    {
      path: 'admin/identity',
      name: 'Identity Service',
    },
    {
      path: 'admin/ledger',
      name: 'Ledger Service',
    },
    {
      path: 'admin/moneyball',
      name: 'Moneyball Service',
    },
    {
      path: 'admin/workforce/quickbuilds',
      name: 'Workforce',
    },
    {
      path: 'admin/firehose',
      name: 'Live Firehose',
    },
    {
      path: 'admin/data-deliveries/report-deliveries',
      name: 'Data Deliveries',
    },
    {
      path: 'datawarehouse/stats',
      name: 'Data Warehouse',
    },
    {
      path: 'admin/providers',
      name: 'Providers',
    },
    {
      path: 'admin/streams/dashboard',
      name: 'Onboarding',
    },
    {
      path: 'admin/billing/subscriptions',
      name: 'Billing',
    },
    {
      path: 'admin/grow',
      name: 'Grow',
    },
    {
      path: 'admin/techno-target',
      name: 'Techno Target',
    },
  ];
  showNav: boolean = false;
  isSmallScreen: boolean = false;
  showAdmin: boolean = false;
  $showAdmin: Observable<boolean>;
  appVersion: string;
  quickSearchValue: string = '';

  private sidenavState: boolean = true; // Track the original state of sidenav
  private drawerSubscription: Subscription;

  constructor(
    private auth: AuthService,
    private router: Router,
    private notify: NotificationService,
    private _clipboardService: ClipboardService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private invService: InvitationsService,
    private _hotkeysService: HotkeysService,
    public streamsDrawerService: StreamsSubscribeDrawerService
  ) {
    this.registerUserHotkeys();
  }
  $orgs: Observable<any>;
  orgs: UIOrg[] = [];
  selectedOrg: any;
  $selectedOrg: Observable<string>;
  $isOrgAdmin: Observable<boolean> = of(false);
  $isOrgActive: Observable<boolean> = of(false);
  $orgCapabilities: Observable<String[]> = of([]);

  toggleSideNav() {
    this.sidenav.toggle();
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200); // emit resize event to other components
  }

  async ngOnInit() {
    this.$isLoggedIn = this.auth.$isLoggedIn;
    this.$selectedOrg = this.auth.$getSelectedOrgId;
    this.$isOrgAdmin = this.auth.$isOrgAdmin;
    this.$isOrgActive = this.auth.$isOrgActive;
    this.$orgCapabilities = this.auth.$getOrgCapabilites;
    this.$showAdmin = this.auth.$isAdmin;
    this.$showAdmin.subscribe((isAdmin) => {
      if (!isAdmin) return;

      this.registerAdminHotkeys();
    });
    this.auth.$getOrgs.subscribe(() => {
      this.orgs = this.auth.getOrgsValue || [];
      this.selectedOrg = this.auth.getSelectedOrgIdValue;
    });
    this.$isLoggedIn.subscribe(() => {
      this.orgs = this.auth.getOrgsValue || [];
      this.selectedOrg = this.auth.getSelectedOrgIdValue;
    });

    this.drawerSubscription = this.streamsDrawerService.isDrawerOpen$.subscribe((isOpen) => {
      if (this.sidenav) {
        if (isOpen) {
          // Store current state before closing
          this.sidenavState = this.sidenav.opened;
          this.sidenav.close();
        } else if (!this.isSmallScreen && this.sidenavState) {
          // Restore previous state if it was open and we're not on small screen
          this.sidenav.open();
        }
      }
    });

    // GLD-specific export options
    Highcharts.setOptions({
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
    });
  }

  ngOnDestroy() {
    if (this.drawerSubscription) {
      this.drawerSubscription.unsubscribe();
    }
  }

  onOrgSelected() {
    this.auth.setSelectedOrgId(this.selectedOrg);
    this.router.navigate(['/main']);
  }

  // Helper method to determine if tooltip should be shown
  getOrgTooltip(orgName: string): string {
    // Only show tooltip if name is longer than ~30 chars (approximate truncation point)
    // You can adjust this threshold based on your panel width
    return orgName && orgName.length > 30 ? orgName : '';
  }

  async logout() {
    this.auth.logOut();
    this.notify.info('Successfully logged out.');
  }

  ngAfterViewInit(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.HandsetPortrait,
        Breakpoints.TabletPortrait,
        Breakpoints.HandsetLandscape,
      ])
      .subscribe((result) => {
        setTimeout(() => {
          this.isSmallScreen = result.matches;
          // side nav doesn't exist in workforce, login etc.
          if (this.sidenav) {
            if (this.isSmallScreen) {
              this.sidenav.close();
            } else {
              this.sidenav.open();
            }
          }
          setTimeout(() => window.dispatchEvent(new Event('resize')), 200); // emit resize event to other components
        });
      });
  }

  triggerRefresh() {
    this.auth.refreshSession().subscribe({
      next: () => {
        this._clipboardService.copyFromContent(this.auth.getAccessTokenValue);
        this.notify.success('Refreshed and copied to clipboard');
      },
      error: () => {
        this.notify.error('Error');
      },
    });
  }

  handleNavigation(url: string): void {
    this.router.navigateByUrl(url);
  }

  quickSearch(): void {
    const input = this.quickSearchValue?.trim();
    if (!input) {
      return;
    }
    this.quickSearchValue = ''; // Clear the input after navigation

    const lower = input.toLowerCase();

    // Org ID -> identity org dashboard
    if (lower.startsWith('o_')) {
      this.router.navigate(['/admin/identity/orgs', input, 'dashboard']);
      return;
    }

    // User ID -> identity users tab with that user selected
    if (lower.startsWith('u_')) {
      this.router.navigate(['/admin/identity'], {
        queryParams: { tab: 'users', userId: input },
      });
      return;
    }

    // Company LinkedIn URL -> DW companies page for that lipid
    const companyUrlMatch = input.match(/linkedin\.com\/company\/([^\/?#]+)/i);
    if (companyUrlMatch) {
      this.router.navigate(['/datawarehouse/companies'], {
        queryParams: { id: companyUrlMatch[1] },
      });
      return;
    }

    // Company ID -> DW companies page
    if (lower.startsWith('ldc-')) {
      this.router.navigate(['/datawarehouse/companies'], { queryParams: { id: input } });
      return;
    }

    // Person LinkedIn URL -> inspect with the lipid
    const personUrlMatch = input.match(/linkedin\.com\/in\/([^\/?#]+)/i);
    if (personUrlMatch) {
      this.router.navigate(['/datawarehouse/inspect'], {
        queryParams: { ref: personUrlMatch[1] },
      });
      return;
    }

    // Person/contact IDs (ldp-, ldlc-) or a naked lipid -> inspect
    this.router.navigate(['/datawarehouse/inspect'], { queryParams: { ref: input } });
  }

  intercomTargetName = 'Sales Inquiry';
  showSupportWindow() {
    (window as any)['Intercom']('showNewMessage');
  }

  showSubscriptionDialog(product: 'moneyball' | 'people') {
    const dialogRef = this.dialog.open(SubscriptionDialogComponent, {
      backdropClass: 'darker-dialog-backdrop',
      panelClass: 'subscription-dialog',
      data: { subscriptionType: product },
    });
    dialogRef.afterClosed().subscribe((data) => {
      Log.d(data);
    });
  }

  // handle the invite dialog form submission
  showInviteDialog(evt?: MouseEvent) {
    evt?.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';
    const dialogRef = this.dialog.open(InvitationsCreateDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((data) => {
      if (!data) return;

      if (data.emails && Array.isArray(data.emails)) {
        // Confirm it's an array
        data.emails.forEach((email: string) => {
          const body = {
            inviteeEmail: email.trim(),
            inviteeRole: data.role,
          };
          const req: CreateShareInvitationRequest = {
            invitee_email: email.trim(),
            invited_by_name: this.auth.getUserValue.name,
            role: data.role,
          };

          this.invService
            .createShareInvitation('gotlivedata', this.auth.getSelectedOrgIdValue, req)
            .subscribe({
              next: (r) => {
                this.notify.success(email + ' successfully invited.');
              },
              error: (err) => {
                if (err.status === 400 && err.error?.message.startsWith('Invalid email')) {
                  this.notify.error('Invalid email: ' + email);
                  return;
                }
                if (err.status === 409) {
                  this.notify.error('User already has access to this org');
                  return;
                }

                this.notify.error('Failed to invite ' + email + '. Please try again later.');
              },
            });
        });
      } else {
        this.notify.error('Error inviting users. Please try again later.');
      }
    });
  }

  private registerAdminHotkeys() {
    this._hotkeysService.add([
      new Hotkey(
        'alt+shift+i',
        (event: KeyboardEvent): boolean => {
          this.router.navigate(['/datawarehouse/inspect']);
          return true;
        },
        undefined,
        'Go to Inspect Page'
      ),
      new Hotkey(
        'alt+shift+o',
        (event: KeyboardEvent): boolean => {
          this.router.navigate(['/admin/identity']);
          return true;
        },
        undefined,
        'Go to Orgs Page'
      ),
      new Hotkey(
        'alt+shift+q',
        (event: KeyboardEvent): boolean => {
          this.inspectPersonAtCursor();
          return true;
        },
        undefined,
        'Inspect Person at Cursor'
      ),
      new Hotkey(
        'alt+shift+c',
        (event: KeyboardEvent): boolean => {
          const currentValue = localStorage.getItem('showChatCharts');
          localStorage.setItem('showChatCharts', currentValue === 'true' ? 'false' : 'true');
          return true;
        },
        undefined,
        'Toggle Chat Charts'
      ),
    ]);
  }

  private registerUserHotkeys() {
    this._hotkeysService.add([
      new Hotkey(
        'alt+shift+r',
        (event: KeyboardEvent): boolean => {
          this.triggerRefresh();
          return true;
        },
        undefined,
        'Refresh Session & Copy Token'
      ),
    ]);
  }

  private inspectPersonAtCursor() {
    const selection = window.getSelection();
    let selectedText = '';
    if (selection && !selection.isCollapsed) {
      selectedText = selection.toString().trim();
    } else {
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;

        if (textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.textContent || '';
          const position = range.startOffset;
          selectedText = this.getWordAtIndex(text, position);
        }
      }
    }
    if (selectedText.startsWith('LDP-')) {
      this.router.navigate(['/datawarehouse/inspect'], {
        queryParams: { ref: selectedText },
      });
    }
  }

  private getWordAtIndex(text: string, index: number): string {
    // Find word boundaries
    const beforeCursor = text.slice(0, index);
    const afterCursor = text.slice(index);

    const wordStartMatch = beforeCursor.match(/\S*$/);
    const wordEndMatch = afterCursor.match(/^\S*/);

    const wordStart = wordStartMatch ? wordStartMatch[0] : '';
    const wordEnd = wordEndMatch ? wordEndMatch[0] : '';

    return wordStart + wordEnd;
  }
  startIntercomSurvey() {
    (window as any)['Intercom']('showNewMessage');
  }

  reloadApp() {
    window.location.reload();
  }
}
